import React, { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';

// ─── Types ────────────────────────────────────────────────────────────────────

type SlideId =
  | 'cover'
  | 'founder'
  | 'problem'
  | 'solution'
  | 'who'
  | 'market'
  | 'ai'
  | 'revenue'
  | 'competitive'
  | 'team'
  | 'ask'
  | 'financials'
  | 'cta';

interface Slide {
  id: SlideId;
  label: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const RED = '#bf1e2e';
const NAVY = '#014c8d';
const BG = '#080d16';
const SURFACE = '#0d1525';
const BORDER = 'rgba(255,255,255,0.08)';
const DIM = 'rgba(255,255,255,0.72)';
const DESIGN_W = 960;
const DESIGN_H = DESIGN_W * 9 / 16; // 540

const SLIDES: Slide[] = [
  { id: 'cover',       label: '01 Cover' },
  { id: 'founder',     label: '02 Founder Story' },
  { id: 'problem',     label: '03 Problem' },
  { id: 'solution',    label: '04 Solution' },
  { id: 'who',         label: '05 Revenue Segments' },
  { id: 'market',      label: '06 Market Size' },
  { id: 'ai',          label: '07 Product & AI' },
  { id: 'revenue',     label: '08 Revenue' },
  { id: 'competitive', label: '09 Competitive Landscape' },
  { id: 'team',        label: '10 Team' },
  { id: 'ask',         label: '11 Ask' },
  { id: 'financials',  label: '12 Financial Projections' },
  { id: 'cta',         label: '13 CTA' },
];

// ─── Scaled slide wrapper (mobile-safe) ───────────────────────────────────────

function ScaledSlide({ children }: { children: React.ReactNode }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setScale(el.offsetWidth / DESIGN_W);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={containerRef} style={{ width: '100%', height: DESIGN_H * scale, position: 'relative' }}>
      <div style={{
        width: DESIGN_W,
        height: DESIGN_H,
        transform: `scale(${scale})`,
        transformOrigin: 'top left',
        position: 'absolute',
        top: 0,
        left: 0,
      }}>
        {children}
      </div>
    </div>
  );
}

// ─── Shared slide layout ──────────────────────────────────────────────────────

function SlideShell({
  tag,
  children,
  center = false,
}: {
  tag?: string;
  children: React.ReactNode;
  center?: boolean;
}) {
  return (
    <div style={{
      width: '100%',
      height: '100%',
      background: BG,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: center ? 'center' : 'flex-start',
      alignItems: center ? 'center' : 'stretch',
      padding: '48px 64px',
      position: 'relative',
      overflow: 'hidden',
      boxSizing: 'border-box',
    }}>
      {/* Subtle grid */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: `
          linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)
        `,
        backgroundSize: '60px 60px',
      }} />
      {/* Red top bar */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: RED }} />
      {/* Slide tag */}
      {tag && (
        <div style={{
          position: 'absolute', top: 20, left: 64,
          fontSize: 10, fontWeight: 800, letterSpacing: 3,
          color: RED, textTransform: 'uppercase',
        }}>{tag}</div>
      )}
      {/* BTHD logo bottom-right */}
      <img
        src="/bthd-email-sig-300x80-transparent.png"
        alt="BTHD"
        style={{ position: 'absolute', bottom: 16, right: 48, height: 20, width: 'auto', opacity: 0.35 }}
      />
      {children}
    </div>
  );
}

function SlideHeading({ children, size = 48 }: { children: React.ReactNode; size?: number }) {
  return (
    <h2 style={{
      margin: '0 0 8px',
      fontSize: size,
      fontWeight: 900,
      lineHeight: 1.05,
      color: '#fff',
      textTransform: 'uppercase',
      letterSpacing: -0.5,
    }}>{children}</h2>
  );
}

function RedAccent({ children }: { children: React.ReactNode }) {
  return <span style={{ color: RED }}>{children}</span>;
}

function Tag({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center',
      background: `${RED}22`, border: `1px solid ${RED}55`,
      borderRadius: 4, padding: '3px 10px',
      fontSize: 10, fontWeight: 800, letterSpacing: 2,
      color: RED, textTransform: 'uppercase', marginBottom: 16,
    }}>{children}</div>
  );
}

// ─── Individual slides ────────────────────────────────────────────────────────

function CoverSlide() {
  return (
    <SlideShell center>
      <div style={{
        position: 'absolute', top: '30%', left: '50%', transform: 'translate(-50%,-50%)',
        width: 600, height: 300,
        background: `radial-gradient(ellipse, ${RED}22 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />
      <div style={{ textAlign: 'center', position: 'relative' }}>
        <div style={{
          fontSize: 11, fontWeight: 800, letterSpacing: 4,
          color: RED, textTransform: 'uppercase', marginBottom: 24,
        }}>Bigger Than Hoop Dreams</div>
        <h1 style={{
          fontSize: 72, fontWeight: 900, lineHeight: 1,
          color: '#fff', textTransform: 'uppercase',
          letterSpacing: -1, margin: '0 0 24px',
        }}>
          Athlete Development<br />
          <span style={{ color: RED }}>and Transition Platform</span>
        </h1>
        <p style={{ fontSize: 18, color: DIM, maxWidth: 540, margin: '0 auto 40px', lineHeight: 1.6 }}>
          An AI-augmented athlete development and transition platform helping amateur, collegiate and professional athletes build a plan and pathway in sports.
        </p>
      </div>
    </SlideShell>
  );
}

function ProblemSlide() {
  const stats = [
    { stat: '~16%', label: 'of retired NFL players declare bankruptcy within 12 years, and career earnings don\'t reduce this risk (NBER)' },
    { stat: '39%',  label: 'of elite athletes face psychological distress after retirement, nearly 2× the rate of general workforce retirees (British Journal of Sports Medicine, 2019)' },
    { stat: '~2M',  label: 'high school athletes age out of organized sport in the US each year, graduating seniors only; 8.27M total participate across all grades (NFHS 2024–25)' },
  ];
  return (
    <SlideShell>
      <div style={{ marginTop: 28 }}>
        <SlideHeading>Missing Layer in <RedAccent>Athletic Development</RedAccent></SlideHeading>
        <p style={{ color: DIM, fontSize: 15, marginBottom: 20, maxWidth: 600, lineHeight: 1.6 }}>
          Athletes spend years building elite discipline, resilience, and performance under pressure.
          Yet there are very limited resources built specifically to help athletes translate those qualities.
        </p>
        <div style={{ display: 'flex', gap: 20 }}>
          {stats.map(({ stat, label }) => (
            <div key={stat} style={{
              flex: 1, background: SURFACE,
              border: `1px solid ${BORDER}`, borderRadius: 12,
              padding: '16px 20px',
            }}>
              <div style={{ fontSize: 40, fontWeight: 900, color: RED, lineHeight: 1, marginBottom: 6 }}>{stat}</div>
              <div style={{ fontSize: 12.5, color: DIM, lineHeight: 1.5 }}>{label}</div>
            </div>
          ))}
        </div>
      </div>
    </SlideShell>
  );
}

function SolutionSlide() {
  return (
    <SlideShell>
      <div style={{ marginTop: 40, display: 'flex', gap: 64, alignItems: 'flex-start' }}>
        <div style={{ flex: 1.2 }}>
          <SlideHeading size={42}>A Structured <RedAccent>Operating System</RedAccent> for Life After Sport</SlideHeading>
          <p style={{ color: DIM, fontSize: 15, lineHeight: 1.7, marginTop: 16 }}>
            BTHD is an AI-augmented athlete development and transition platform helping amateur, collegiate and professional athletes build a plan and pathway in sports. Using a behavioural-based assessment methodology,
            BTHD delivers structured programs and pathways across athlete assessment, skills and pathways, leadership
            development, and lasting impact, accessible via web and mobile.
          </p>
          <p style={{ color: DIM, fontSize: 15, lineHeight: 1.7, marginTop: 12 }}>
            Institutions license access for their athletes. Athletes get a structured roadmap.
          </p>
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[
            ['Institution licenses BTHD', 'School, university, or sports org buys access'],
            ['Athletes onboard + assess', 'Identity framework reveals archetype + strengths'],
            ['AI drives personalization', 'Career paths, coaching, and transition analysis'],
            ['Outcomes tracked', 'Transition readiness and legacy metrics for institutions'],
          ].map(([title, body]) => (
            <div key={title} style={{
              background: SURFACE, border: `1px solid ${BORDER}`,
              borderRadius: 8, padding: '14px 18px',
            }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: '#fff', marginBottom: 3 }}>{title}</div>
              <div style={{ fontSize: 12, color: DIM }}>{body}</div>
            </div>
          ))}
        </div>
      </div>
    </SlideShell>
  );
}

function WhoSlide() {
  const segments = [
    { title: 'High Schools & Prep Schools', color: RED,  body: 'NIL readiness, athlete wellness, and post-sport support before the next recruiting cycle. Separate Early Pathways portal for under-18 athletes.',                     tag: 'Early Pathways' },
    { title: 'Colleges & Universities',     color: RED,  body: 'Transition compliance, duty-of-care data, and career readiness tracking. Athletic directors need outcomes evidence for regulators, funders, and recruiting.',          tag: 'University' },
    { title: 'Sports Orgs & Associations',  color: RED,  body: 'At-risk monitoring and transition readiness across active rosters. Player associations need structured post-career support for membership retention.',                    tag: 'Sports Org' },
    { title: 'Corporations',               color: NAVY, body: 'Anonymized talent pipeline: pre-assessed ex-athletes filtered by sport, archetype, and readiness score. Hire disciplined, high-performing candidates.',                  tag: 'Talent' },
    { title: 'Wealth Management Firms',    color: NAVY, body: 'Pre-retirement athlete engagement before assets are mismanaged. BTHD surfaces retiring athletes at the moment of transition, when financial decisions are highest-stakes.', tag: 'Wealth' },
    { title: 'Individual Athletes (18+)',   color: NAVY, body: 'Self-directed transition via iOS and Android. Free tier drives individual acquisition; institutional licensing follows as athletes share the platform with their programs.',  tag: 'Mobile' },
  ];
  return (
    <SlideShell>
      <div style={{ marginTop: 28 }}>
        <SlideHeading size={38}>Six Distinct <RedAccent>Revenue Segments</RedAccent></SlideHeading>
        <p style={{ color: DIM, fontSize: 13, lineHeight: 1.6, marginBottom: 14, marginTop: 4 }}>
          Each segment has a distinct buyer, a distinct pain point, and a distinct willingness to pay. Institutions license access for their athletes. Individual app users without an institution license pay on subscription.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
          {segments.map(({ title, color, body, tag }) => (
            <div key={title} style={{
              background: SURFACE, border: `1px solid ${BORDER}`,
              borderTop: `2px solid ${color}`,
              borderRadius: 8, padding: '12px 14px',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <div style={{ width: 7, height: 7, borderRadius: '50%', background: color }} />
                <span style={{
                  fontSize: 9, fontWeight: 800, letterSpacing: 1,
                  color, background: `${color}18`, border: `1px solid ${color}44`,
                  borderRadius: 3, padding: '2px 6px', textTransform: 'uppercase',
                }}>{tag}</span>
              </div>
              <div style={{ fontSize: 12.5, fontWeight: 800, color: '#fff', marginBottom: 5 }}>{title}</div>
              <div style={{ fontSize: 10.5, color: DIM, lineHeight: 1.5 }}>{body}</div>
            </div>
          ))}
        </div>
      </div>
    </SlideShell>
  );
}

function MarketSlide() {
  const tiers = [
    {
      label: 'TAM',
      title: 'Total Addressable Market',
      value: '~$87M USD/yr',
      sub: 'North America · All Formal Institutions',
      color: RED,
      points: [
        'HS + NCAA/NAIA/NJCAA + USports/CCAA',
        '~9M formal athletes (NFHS 8.27M + NCAA 554K + NAIA 83K + NJCAA 60K)',
        '~25,000+ formal athletic institutions across North America',
      ],
      source: 'NFHS 2024–25 · NCAA 2024–25 · NAIA · NJCAA · USports.ca · CCAA.ca',
    },
    {
      label: 'SAM',
      title: 'Serviceable Addressable Market',
      value: '~$30M USD/yr',
      sub: '~3,500 Beachhead Institutions',
      color: NAVY,
      points: [
        'NCAA + NAIA + NJCAA + USports + CCAA + private HS',
        'Institutions with formal athletic programs and transition needs',
        'Average license ~$8,700 USD/yr per institution',
      ],
      source: 'BTHD revenue model · NCAA, NAIA, NJCAA directories · USports.ca · CCAA.ca',
    },
    {
      label: 'SOM',
      title: 'Serviceable Obtainable Market',
      value: '~$183K USD',
      sub: 'By Year 3 · Conservative',
      color: '#1a6b3c',
      points: [
        'Year 1: 3 institutions → $22,100 USD',
        'Year 2: ~7 institutions → $65,800 USD',
        'Year 3: ~14 institutions → $183,100 USD',
        'First cash-flow positive Year 3',
      ],
      source: 'BTHD 3-year financial model (conservative case)',
    },
  ];

  return (
    <SlideShell>
      <div style={{ marginTop: 24 }}>
        <Tag>TAM · SAM · SOM</Tag>
        <SlideHeading size={36}>A <RedAccent>$87M+</RedAccent> USD North American Market For Athlete Development and Transition</SlideHeading>
        <div style={{ display: 'flex', gap: 16, marginTop: 20 }}>
          {tiers.map(({ label, title, value, sub, color, points, source }) => (
            <div key={label} style={{
              flex: 1,
              background: SURFACE,
              border: `1px solid ${BORDER}`,
              borderTop: `3px solid ${color}`,
              borderRadius: 10,
              padding: '18px 20px',
              display: 'flex',
              flexDirection: 'column',
            }}>
              <div style={{
                fontSize: 10, fontWeight: 900, letterSpacing: 3,
                color, textTransform: 'uppercase', marginBottom: 6,
              }}>{label}</div>
              <div style={{ fontSize: 11, color: DIM, marginBottom: 10, lineHeight: 1.3 }}>{title}</div>
              <div style={{ fontSize: 28, fontWeight: 900, color, lineHeight: 1, marginBottom: 2 }}>{value}</div>
              <div style={{ fontSize: 10, fontWeight: 700, color: DIM, marginBottom: 14, letterSpacing: 0.5 }}>{sub}</div>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
                {points.map(pt => (
                  <div key={pt} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <div style={{ width: 5, height: 5, borderRadius: '50%', background: color, flexShrink: 0, marginTop: 5 }} />
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.82)', lineHeight: 1.45 }}>{pt}</div>
                  </div>
                ))}
              </div>
              <div style={{
                fontSize: 9, color: 'rgba(255,255,255,0.6)', lineHeight: 1.4,
                borderTop: `1px solid ${BORDER}`, paddingTop: 8, fontStyle: 'italic',
              }}>{source}</div>
            </div>
          ))}
        </div>
      </div>
    </SlideShell>
  );
}

function PhoneMockup({ screenshot, alt }: { screenshot: string; alt: string }) {
  return (
    <div style={{
      width: 145,
      height: 310,
      background: '#080808',
      borderRadius: 30,
      border: '1.5px solid rgba(255,255,255,0.16)',
      boxShadow: '0 0 0 1px rgba(0,0,0,1), 0 20px 60px rgba(0,0,0,0.9)',
      padding: '8px 4px 6px',
      position: 'relative',
      boxSizing: 'border-box',
      flexShrink: 0,
    }}>
      <div style={{ position: 'absolute', top: 9, left: '50%', transform: 'translateX(-50%)', width: 32, height: 8, background: '#000', borderRadius: 4, zIndex: 3 }} />
      <div style={{ width: '100%', height: '100%', borderRadius: 24, overflow: 'hidden', background: '#000' }}>
        <img src={screenshot} alt={alt} style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
      </div>
    </div>
  );
}

function AISlide() {
  const stages = [
    { num: '01', label: 'Athlete Assessment',      color: RED,       screenshot: '/screenshots/07-identity-profile.jpg' },
    { num: '02', label: 'Skills and Pathways',    color: NAVY,      screenshot: '/screenshots/08-transition-path.jpg'  },
    { num: '03', label: 'Leadership Development', color: '#1a6b3c', screenshot: '/screenshots/03-leadership.jpg'       },
    { num: '04', label: 'Lasting Impact',         color: '#7a3c8a', screenshot: '/screenshots/05-legacy.jpg'           },
  ];
  return (
    <SlideShell>
      <div style={{ marginTop: 12 }}>
        <SlideHeading size={30}>Four Stages. <RedAccent>One AI-Powered Journey.</RedAccent></SlideHeading>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginTop: 12 }}>
          {stages.map(({ num, label, color, screenshot }) => (
            <div key={num} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <PhoneMockup screenshot={screenshot} alt={label} />
              <div style={{ marginTop: 10, textAlign: 'center' }}>
                <div style={{ fontSize: 9, fontWeight: 800, color, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 2 }}>{num}</div>
                <div style={{ fontSize: 9.5, fontWeight: 700, color: 'rgba(255,255,255,0.82)', letterSpacing: 0.8, textTransform: 'uppercase' }}>{label}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 8.5, color: DIM, letterSpacing: 0.5 }}>5-Dimension Behavioral Assessment · Transferable Skills · AI-Powered Sport-to-Career Mapping</div>
        </div>
      </div>
    </SlideShell>
  );
}

function RevenueSlide() {
  const streams = [
    { num: '01', name: 'Institutional Licensing',        who: 'Schools, universities, sports orgs',   horizon: 'NOW',       color: RED },
    { num: '02', name: 'Individual Subscriptions',       who: 'Athletes 18+ · $4.99 CAD/mo via Apple IAP + Google Play', horizon: 'NOW',       color: RED },
    { num: '03', name: 'Partner Sponsorships',           who: 'Wealth firms, corporates',              horizon: 'Near-term', color: '#e07b00' },
    { num: '04', name: 'Referral Fees',                  who: 'Financial, legal, service providers',  horizon: 'Near-term', color: '#e07b00' },
    { num: '05', name: 'Placement Fees',                 who: 'Corporates hiring athletes',            horizon: 'Growth',    color: NAVY },
    { num: '06', name: 'Corporate Talent Pipeline',      who: 'Corporates accessing athlete pool',     horizon: 'Growth',    color: NAVY },
    { num: '07', name: 'Curriculum & Content Licensing', who: 'Associations, universities',            horizon: 'Growth',    color: NAVY },
    { num: '08', name: 'Event Sponsorship & Revenue',    who: 'Corporates, sponsors, ticket buyers',  horizon: 'Growth',    color: NAVY },
  ];
  return (
    <SlideShell>
      <div style={{ marginTop: 24 }}>
        <SlideHeading size={32}>Diversified <RedAccent>B2B2C</RedAccent> Revenue Model</SlideHeading>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 12 }}>
          {streams.map(({ num, name, who, horizon, color }) => (
            <div key={num} style={{
              display: 'flex', alignItems: 'center', gap: 14,
              background: SURFACE, border: `1px solid ${BORDER}`,
              borderLeft: `3px solid ${color}`,
              borderRadius: 6, padding: '8px 14px',
            }}>
              <div style={{ fontSize: 10, fontWeight: 900, color, width: 22, flexShrink: 0 }}>{num}</div>
              <div style={{ flex: 1, fontSize: 12, fontWeight: 700, color: '#fff' }}>{name}</div>
              <div style={{ fontSize: 11, color: DIM, flex: 1.2 }}>{who}</div>
              <div style={{
                fontSize: 8, fontWeight: 800, letterSpacing: 1,
                color: color, background: `${color}18`, border: `1px solid ${color}44`,
                borderRadius: 3, padding: '3px 8px', textTransform: 'uppercase', flexShrink: 0,
              }}>{horizon}</div>
            </div>
          ))}
        </div>
      </div>
    </SlideShell>
  );
}


function FounderSlide() {
  const milestones = [
    { year: 'U SPORTS',             label: 'Saint Mary\'s University, Basketball' },
    { year: 'NBA',                  label: 'Front Office Player Development · Toronto Raptors' },
    { year: 'Technology / Software',label: 'Head of People' },
    { year: 'Non-Profit',           label: 'Leadership & Community · HarryCares Foundation' },
    { year: 'CIBC',                 label: "Canada's Big 5 Bank · Enterprise Strategic partnerships" },
    { year: 'AI & Business Strategy', label: 'Managing AI Projects' },
    { year: 'BTHD',                 label: 'Founder · SaaS AI Athlete Transition Platform' },
  ];
  return (
    <SlideShell>
      <img src="/harry-ballspin.jpg" alt="" aria-hidden style={{
        position: 'absolute', inset: 0, width: '100%', height: '100%',
        objectFit: 'cover', opacity: 0.12, pointerEvents: 'none', userSelect: 'none',
      }} />
      <div style={{ marginTop: 32, display: 'flex', gap: 56, alignItems: 'flex-start', position: 'relative' }}>
        <div style={{ flex: 1 }}>
          <SlideHeading size={38}>Built From<br /><RedAccent>Experience. Not Theory.</RedAccent></SlideHeading>
          <div style={{
            marginTop: 20, padding: '18px 20px',
            background: `${RED}10`, borderLeft: `3px solid ${RED}`,
            borderRadius: 8,
          }}>
            <p style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.85)', lineHeight: 1.75, margin: 0 }}>
              "I experienced firsthand the identity loss that comes post sport competition.
              My time in the Toronto Raptors Front Office, coaching staff and other Player Development
              responsibilities gave me an inside look at the systemic gaps in athlete transition.
              After moving into tech and leading enterprise strategic partnerships at one of Canada's
              Big 5 banks, I realized the problem wasn't a lack of talent, but a lack of transitional
              support for athletes. <strong style={{ color: '#fff' }}>BTHD is the platform I wish I had on my fingertips during my playing days.</strong>"
            </p>
          </div>
          <p style={{ fontSize: 11, color: DIM, marginTop: 12, fontStyle: 'italic' }}>
            Full narrative: hardwood → front office → non-profit → Big 5 bank → AI strategy → BTHD
          </p>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 24 }}>
            <img src="/harry.jpg" alt="Harry Ezenibe" style={{
              width: 64, height: 64, borderRadius: '50%',
              border: `2px solid ${RED}`, objectFit: 'cover', flexShrink: 0,
            }} />
            <div>
              <div style={{ fontSize: 16, fontWeight: 900, color: '#fff' }}>Harry Ezenibe</div>
              <div style={{ fontSize: 12, color: RED, fontWeight: 700 }}>Founder, BTHD</div>
              <div style={{ fontSize: 11, color: DIM, marginTop: 2 }}>Kitchener, Ontario, Canada</div>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {milestones.map(({ year, label }, i) => (
              <div key={year} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                  <div style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: i === milestones.length - 1 ? RED : 'rgba(255,255,255,0.3)',
                    marginTop: 4,
                  }} />
                  {i < milestones.length - 1 && (
                    <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.1)', marginTop: 2 }} />
                  )}
                </div>
                <div style={{ paddingBottom: 4 }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: i === milestones.length - 1 ? RED : DIM, letterSpacing: 1, textTransform: 'uppercase' }}>{year}</div>
                  <div style={{ fontSize: 12, color: i === milestones.length - 1 ? '#fff' : 'rgba(255,255,255,0.82)', marginTop: 1 }}>{label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </SlideShell>
  );
}

function CompetitiveSlide() {
  const competitors: {
    name: string;
    funding: string;
    fullLifecycle: boolean;
    aiPowered: boolean;
    canadaMarket: boolean;
    b2b2c: boolean | null;
    highlight?: boolean;
  }[] = [
    { name: 'BTHD',         funding: 'Bootstrapped', fullLifecycle: true,  aiPowered: true,  canadaMarket: true,  b2b2c: true,  highlight: true },
    { name: 'Teamworks',    funding: '$411M USD',    fullLifecycle: false, aiPowered: true,  canadaMarket: false, b2b2c: false },
    { name: 'ACT Group',    funding: '$1M USD',      fullLifecycle: false, aiPowered: false, canadaMarket: false, b2b2c: false },
    { name: 'PAC',          funding: '$7.6M USD',    fullLifecycle: false, aiPowered: false, canadaMarket: false, b2b2c: false },
    { name: 'Shift Group',  funding: '$10M USD',     fullLifecycle: false, aiPowered: false, canadaMarket: false, b2b2c: false },
    { name: 'Free Agent',   funding: '$1.7M USD',    fullLifecycle: false, aiPowered: true,  canadaMarket: true,  b2b2c: false },
    { name: 'MOGL',         funding: '$5.5M USD',    fullLifecycle: false, aiPowered: true,  canadaMarket: false, b2b2c: true  },
    { name: 'Game Plan CA', funding: 'Govt-funded',  fullLifecycle: false, aiPowered: false, canadaMarket: true,  b2b2c: false },
  ];

  const Check = ({ val }: { val: boolean | null }) => {
    if (val === null) return (
      <div style={{ textAlign: 'center', fontSize: 12, color: 'rgba(255,255,255,0.35)', fontWeight: 700 }}>?</div>
    );
    return (
      <div style={{
        width: 20, height: 20, borderRadius: '50%',
        background: val ? `${RED}22` : 'rgba(255,255,255,0.04)',
        border: `1px solid ${val ? RED : 'rgba(255,255,255,0.1)'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, color: val ? RED : 'rgba(255,255,255,0.2)', fontWeight: 900,
        flexShrink: 0, margin: '0 auto',
      }}>{val ? '✓' : '✗'}</div>
    );
  };

  return (
    <SlideShell>
      <div style={{ marginTop: 16 }}>
        <SlideHeading size={34}><RedAccent>Competitors in This Market.</RedAccent></SlideHeading>
        <div style={{
          marginTop: 8, marginBottom: 14, padding: '7px 14px',
          background: `${RED}12`, border: `1px solid ${RED}33`, borderRadius: 8,
          fontSize: 11.5, color: 'rgba(255,255,255,0.8)', lineHeight: 1.5,
        }}>
          Game Plan Canada is government-funded and restricted to national-team athletes only. Teamworks is an institutional operations platform focused on scheduling, compliance, and team management. Free Agent is a human-curated athlete network. PAC, Shift Group, and ACT Group each address a single stage of the athlete career journey. MOGL is an AI-powered NIL marketplace.
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: `1px solid rgba(255,255,255,0.1)` }}>
              <th style={{ textAlign: 'left', fontSize: 10, fontWeight: 800, color: DIM, letterSpacing: 0.8, textTransform: 'uppercase', padding: '4px 8px 8px', width: '22%' }}>Competitor</th>
              <th style={{ textAlign: 'left', fontSize: 10, fontWeight: 800, color: DIM, letterSpacing: 0.8, textTransform: 'uppercase', padding: '4px 8px 8px', width: '20%' }}>Funding Raised</th>
              {(['Full Lifecycle Focus', 'AI-Powered', 'Canada Market', 'B2B2C'] as const).map(label => (
                <th key={label} style={{ textAlign: 'center', fontSize: 10, fontWeight: 800, color: DIM, letterSpacing: 0.8, textTransform: 'uppercase', padding: '4px 4px 8px' }}>{label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {competitors.map(({ name, funding, fullLifecycle, aiPowered, canadaMarket, b2b2c, highlight }) => (
              <tr key={name} style={{
                background: highlight ? `${RED}15` : 'rgba(255,255,255,0.02)',
                borderTop: `1px solid rgba(255,255,255,${highlight ? '0.12' : '0.05'})`,
              }}>
                <td style={{ fontSize: 12, fontWeight: highlight ? 900 : 600, color: highlight ? '#fff' : 'rgba(255,255,255,0.88)', padding: '6px 8px' }}>{name}</td>
                <td style={{ fontSize: 11, color: highlight ? RED : DIM, padding: '6px 8px', fontVariantNumeric: 'tabular-nums' }}>{funding}</td>
                <td style={{ padding: '6px 4px' }}><Check val={fullLifecycle} /></td>
                <td style={{ padding: '6px 4px' }}><Check val={aiPowered} /></td>
                <td style={{ padding: '6px 4px' }}><Check val={canadaMarket} /></td>
                <td style={{ padding: '6px 4px' }}><Check val={b2b2c} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.6)', fontStyle: 'italic', marginTop: 8 }}>
          Funding figures represent publicly reported amounts raised. Sources: Teamworks $411M, Tracxn (2026); Free Agent $1.7M USD, MarketScale, July 24, 2026; Shift Group $10M, SignalBase/PitchBook; ACT Group $1M, PRNewswire, October 2024; PAC $7.6M, PRNewswire, June 2025; MOGL $5.5M, Axios (2023); Game Plan Canada, government-funded (COC). Teamworks AI-Powered ✓ reflects operational and analytics AI, not athlete transition programming. Free Agent AI-Powered ✓ reflects background matching and recommendation algorithms. Source: MarketScale, July 24, 2026.
        </div>
      </div>
    </SlideShell>
  );
}

function AskSlide() {
  const funds = [
    { label: 'GTM & Sales',    pct: 25, amount: '$60,000',  color: RED,       note: '' },
    { label: 'Tech Build-Out', pct: 20, amount: '$48,000',  color: NAVY,      note: '' },
    { label: 'Curriculum Dev', pct: 12, amount: '$29,000',  color: '#1a6b3c', note: '' },
    { label: 'Founder Salary', pct: 34, amount: '$82,000',  color: '#7a3c8a', note: '$41K/year × 2-year runway' },
    { label: 'Operations',     pct:  9, amount: '$21,000',  color: '#b06a00', note: '' },
  ];
  return (
    <SlideShell>
      <div style={{ marginTop: 36, display: 'flex', gap: 56, alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <SlideHeading size={40}>Raising <RedAccent>$240K USD</RedAccent><br />to Close the First 3.</SlideHeading>
          <p style={{ fontSize: 14, color: DIM, lineHeight: 1.7, margin: '16px 0 24px' }}>
            Pre-seed capital to execute the institutional GTM, scale the platform,
            and build the curriculum foundation for multi-year contracts.
          </p>
          <div style={{ display: 'flex', gap: 12 }}>
            {[
              { label: 'Runway', value: '24 months' },
              { label: 'Break-even', value: 'Year 3 (2028)' },
            ].map(({ label, value }) => (
              <div key={label} style={{
                flex: 1, padding: '12px 16px',
                background: `${RED}12`, border: `1px solid ${RED}33`, borderRadius: 8,
              }}>
                <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 2, color: RED, textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#fff' }}>{value}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: DIM, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 14 }}>
            Use of Funds: $240,000 USD
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {funds.map(({ label, pct, amount, color, note }) => (
              <div key={label}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                  <div>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#fff' }}>{label}</span>
                    {note && <span style={{ fontSize: 10, color: DIM, marginLeft: 8 }}>{note}</span>}
                  </div>
                  <div style={{ fontSize: 12, color: DIM }}>{pct}% · {amount}</div>
                </div>
                <div style={{ height: 6, background: 'rgba(255,255,255,0.06)', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${pct * 3}%`, background: color, borderRadius: 3 }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </SlideShell>
  );
}

function FinancialsSlide() {
  const rows = [
    { stream: 'Institutional Licensing', y1: '$21,600', y2: '$55,400',  y3: '$125,200', highlight: false },
    { stream: 'Individual Subscriptions', y1: '$500',    y2: '$3,200',   y3: '$15,100',  highlight: false },
    { stream: 'Partner Sponsorships',    y1: '$0',      y2: '$5,400',   y3: '$21,600',  highlight: false },
    { stream: 'Other Streams',           y1: '$0',      y2: '$1,800',   y3: '$21,200',  highlight: false },
    { stream: 'Total Revenue',           y1: '$22,100', y2: '$65,800',  y3: '$183,100', highlight: true  },
    { stream: 'Operating Costs',         y1: '$79,100', y2: '$98,600',  y3: '$147,500', highlight: false },
    { stream: 'Net Result',              y1: '($57,000)',y2: '($32,800)',y3: '+$35,600', highlight: true  },
  ];
  return (
    <SlideShell>
      <div style={{ marginTop: 28 }}>
        <SlideHeading size={38}>Conservative Path to <RedAccent>Cash-Flow Positive.</RedAccent></SlideHeading>
        <div style={{ marginTop: 20, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', fontSize: 10, fontWeight: 800, color: DIM, letterSpacing: 1, textTransform: 'uppercase', padding: '8px 0', width: '40%' }}>Revenue Stream</th>
                {['2026, Year 1', '2027, Year 2', '2028, Year 3'].map(yr => (
                  <th key={yr} style={{ textAlign: 'right', fontSize: 10, fontWeight: 800, color: DIM, letterSpacing: 0.5, padding: '8px 12px' }}>{yr}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(({ stream, y1, y2, y3, highlight }, i) => {
                const isSeparator = stream === 'Total Revenue' || stream === 'Operating Costs';
                return (
                  <tr key={stream} style={{
                    background: highlight ? `${RED}10` : i % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent',
                    borderTop: isSeparator ? `1px solid rgba(255,255,255,0.1)` : 'none',
                  }}>
                    <td style={{ fontSize: highlight ? 13 : 12, fontWeight: highlight ? 800 : 400, color: highlight ? '#fff' : 'rgba(255,255,255,0.82)', padding: '7px 0' }}>{stream}</td>
                    <td style={{ textAlign: 'right', fontSize: highlight ? 13 : 12, fontWeight: highlight ? 800 : 400, color: highlight ? '#fff' : DIM, padding: '7px 12px', fontVariantNumeric: 'tabular-nums' }}>{y1}</td>
                    <td style={{ textAlign: 'right', fontSize: highlight ? 13 : 12, fontWeight: highlight ? 800 : 400, color: highlight ? '#fff' : DIM, padding: '7px 12px', fontVariantNumeric: 'tabular-nums' }}>{y2}</td>
                    <td style={{ textAlign: 'right', fontSize: highlight ? 13 : 12, fontWeight: highlight ? 800 : 400, color: stream === 'Net Result' ? '#22c55e' : highlight ? '#fff' : DIM, padding: '7px 12px', fontVariantNumeric: 'tabular-nums' }}>{y3}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', gap: 12, marginTop: 18 }}>
          {[
            { label: '3 institutions → Year 1', sub: 'Starter tier · ~$7,200 USD each' },
            { label: '198% revenue growth', sub: 'Year 1 → Year 2' },
            { label: 'First surplus: Year 3', sub: '+$35,600 USD net operating' },
          ].map(({ label, sub }) => (
            <div key={label} style={{
              flex: 1, padding: '10px 14px',
              background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 8,
            }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#fff', marginBottom: 3 }}>{label}</div>
              <div style={{ fontSize: 10.5, color: DIM }}>{sub}</div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 10, fontSize: 10, color: 'rgba(255,255,255,0.6)', fontStyle: 'italic' }}>
          All figures USD (CAD converted at ~1.39). Apple IAP 15% commission (Small Business Program) applied to individual subscription net revenue.
        </div>
      </div>
    </SlideShell>
  );
}

function TeamSlide() {
  const members = [
    {
      photo: '/harry.jpg',
      name: 'Harry Ezenibe',
      role: 'Founder',
      bio: (
        <>
          Former athlete, entrepreneur, corporate and technology leader. Past experience includes{' '}
          <a href="https://www.smu.ca/withoutlimits/basketball-empowerment.html" target="_blank" rel="noopener noreferrer" style={{ color: RED, textDecoration: 'underline' }}>U SPORTS at Saint Mary's University</a>
          , NBA Elite Camps and Toronto Raptors Front Office Player Development. His journey and impact across Serbia, France, Italy, Mexico, Angola, Nigeria, Canada, USA and the Dominican Republic enable him to identify the gaps that exist in athlete transition. Harry brings firsthand knowledge of athlete transition to every product decision at BTHD. He founded BTHD to create the platform he wished existed, a structured system for what comes after sports.
        </>
      ),
    },
    {
      photo: '/osa.jpg',
      name: 'Osa Wilson',
      role: 'Athlete Legacy Ambassador',
      bio: 'NCAA and CEBL athlete turned financial industry professional. Osa is living proof that BTHD framework works. He was directly mentored by Harry through BTHD behavioural assessment methodology from an athlete to a thriving professional. He represents the athlete voice at the core of everything BTHD builds.',
    },
  ];
  return (
    <SlideShell>
      <div style={{ display: 'flex', gap: 40, alignItems: 'stretch', flex: 1, minHeight: 0 }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0 }}>
          <SlideHeading size={38}>Founded By<br /><RedAccent>Athletes.</RedAccent></SlideHeading>
          <p style={{ color: DIM, fontSize: 13, lineHeight: 1.65, margin: 0 }}>
            The team brings together lived experience in sport with expertise in
            corporate finance, technology, business leadership, and community building.
          </p>
          <img src="/harry-osa.jpg" alt="Harry Ezenibe and Osa Wilson" style={{
            width: '100%', height: 'auto', display: 'block', borderRadius: 8,
          }} />
        </div>
        <div style={{ flex: 1.2, display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0 }}>
          {members.map(({ photo, name, role, bio }) => (
            <div key={name} style={{
              flex: 1, minHeight: 0,
              background: SURFACE, border: `1px solid ${BORDER}`,
              borderRadius: 12, padding: '14px 18px',
              display: 'flex', flexDirection: 'column',
            }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8, flexShrink: 0 }}>
                <img src={photo} alt={name} style={{
                  width: 46, height: 46, borderRadius: '50%',
                  border: `2px solid ${RED}`, objectFit: 'cover', flexShrink: 0,
                }} />
                <div>
                  <div style={{ fontSize: 14, fontWeight: 900, color: '#fff' }}>{name}</div>
                  <div style={{ fontSize: 11, color: RED, fontWeight: 700 }}>{role}</div>
                </div>
              </div>
              <p style={{ fontSize: 11, color: DIM, lineHeight: 1.55, margin: 0 }}>{bio as React.ReactNode}</p>
            </div>
          ))}
        </div>
      </div>
    </SlideShell>
  );
}

function CTASlide() {
  return (
    <SlideShell center>
      <div style={{
        position: 'absolute', top: '20%', left: '50%', transform: 'translate(-50%,-50%)',
        width: 700, height: 400,
        background: `radial-gradient(ellipse, ${RED}18 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />
      <div style={{ textAlign: 'center', position: 'relative', maxWidth: 680 }}>
        <h1 style={{
          fontSize: 60, fontWeight: 900, lineHeight: 1.05,
          color: '#fff', textTransform: 'uppercase',
          letterSpacing: -1, margin: '0 0 20px',
        }}>
          Join the <RedAccent>Pre-Seed</RedAccent><br />Round.
        </h1>
        <p style={{ fontSize: 16, color: DIM, lineHeight: 1.7, marginBottom: 36, maxWidth: 560, margin: '0 auto 36px' }}>
          BTHD is raising $240K USD to close our first three institutional contracts
          and build the curriculum foundation for multi-year deals.
          If you want to be part of what comes after sport, let's talk.
        </p>
        <div style={{ display: 'flex', gap: 32, justifyContent: 'center', alignItems: 'center', marginBottom: 36, flexWrap: 'wrap' }}>
          <a href="https://cal.com/bigger-than-hoop-dreams" target="_blank" rel="noopener noreferrer" style={{
            fontSize: 15, color: '#fff', textDecoration: 'underline',
          }}>
            Schedule a call: cal.com/bigger-than-hoop-dreams
          </a>
          <span style={{ fontSize: 15, color: DIM }}>harry@bthd.app</span>
        </div>
        <div style={{ display: 'flex', gap: 24, justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ fontSize: 12, color: DIM }}>bthd.app</div>
          <div style={{ fontSize: 12, color: DIM }}>Kitchener, ON, Canada</div>
          <div style={{ width: 1, height: 16, background: BORDER }} />
          <div style={{ fontSize: 10, color: DIM, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>Supported By</div>
          <img src="/accelerator-centre.jpg" alt="Accelerator Centre" style={{ height: 22, width: 'auto', opacity: 0.75, borderRadius: 2 }} />
          <img src="/innovation-factory-logo.png" alt="Innovation Factory" style={{ height: 22, width: 'auto', opacity: 0.75 }} />
        </div>
      </div>
    </SlideShell>
  );
}

// ─── Slide registry ───────────────────────────────────────────────────────────

const SLIDE_COMPONENTS: Record<SlideId, React.ComponentType> = {
  cover:       CoverSlide,
  founder:     FounderSlide,
  problem:     ProblemSlide,
  solution:    SolutionSlide,
  who:         WhoSlide,
  market:      MarketSlide,
  ai:          AISlide,
  revenue:     RevenueSlide,
  competitive: CompetitiveSlide,
  team:        TeamSlide,
  ask:         AskSlide,
  financials:  FinancialsSlide,
  cta:         CTASlide,
};

// ─── Main page ────────────────────────────────────────────────────────────────

export default function DeckPage() {
  const [authed, setAuthed] = useState(() => sessionStorage.getItem('deck_auth') === '1');
  const [pw, setPw] = useState('');
  const [pwError, setPwError] = useState(false);
  const [activeSlide, setActiveSlide] = useState<SlideId>('cover');
  const [mode, setMode] = useState<'builder' | 'present'>('builder');
  const [navOpen, setNavOpen] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const ActiveComponent = SLIDE_COMPONENTS[activeSlide];
  const currentIndex = SLIDES.findIndex(s => s.id === activeSlide);

  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setFullscreen(false); return; }
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || e.key === ' ') {
        e.preventDefault();
        setActiveSlide(prev => {
          const idx = SLIDES.findIndex(s => s.id === prev);
          return idx < SLIDES.length - 1 ? SLIDES[idx + 1].id : prev;
        });
      }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveSlide(prev => {
          const idx = SLIDES.findIndex(s => s.id === prev);
          return idx > 0 ? SLIDES[idx - 1].id : prev;
        });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullscreen]);

  const handleUnlock = () => {
    if (pw === 'bthdinvestor') {
      sessionStorage.setItem('deck_auth', '1');
      setAuthed(true);
      setPwError(false);
    } else {
      setPwError(true);
      setPw('');
    }
  };

  if (!authed) {
    return (
      <div style={{
        minHeight: '100vh', background: '#040810',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: "'Barlow', -apple-system, BlinkMacSystemFont, sans-serif",
      }}>
        <div style={{ textAlign: 'center', maxWidth: 360, width: '90%' }}>
          <img src="/bthd-email-sig-300x80-transparent.png" alt="BTHD"
            style={{ height: 28, width: 'auto', marginBottom: 32 }} />
          <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: 2, color: RED, textTransform: 'uppercase', marginBottom: 8 }}>
            Investor Deck
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 900, color: '#fff', marginBottom: 8, lineHeight: 1.2 }}>
            Confidential Access
          </h1>
          <p style={{ fontSize: 13, color: DIM, lineHeight: 1.6, marginBottom: 32 }}>
            This deck is password protected. Enter the access code to continue.
          </p>
          <input
            type="password"
            value={pw}
            onChange={e => { setPw(e.target.value); setPwError(false); }}
            onKeyDown={e => e.key === 'Enter' && handleUnlock()}
            placeholder="Access code"
            autoFocus
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '12px 16px', borderRadius: 8,
              border: `1px solid ${pwError ? RED : BORDER}`,
              background: SURFACE, color: '#fff', fontSize: 14,
              outline: 'none', marginBottom: 8,
            }}
          />
          {pwError && (
            <div style={{ fontSize: 12, color: RED, marginBottom: 8 }}>Incorrect access code.</div>
          )}
          <button
            onClick={handleUnlock}
            style={{
              width: '100%', padding: '12px', borderRadius: 8,
              background: RED, border: 'none', color: '#fff',
              fontSize: 14, fontWeight: 800, cursor: 'pointer', letterSpacing: 1,
            }}
          >Enter</button>
        </div>
      </div>
    );
  }

  const handleExportPDF = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf'),
      ]);

      const wrapper = document.createElement('div');
      wrapper.style.cssText = `position:fixed;top:0;left:100vw;width:${DESIGN_W}px;height:${DESIGN_H}px;overflow:hidden;z-index:-1;font-family:'Barlow','Barlow Condensed',sans-serif;`;
      document.body.appendChild(wrapper);

      const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: [DESIGN_W, DESIGN_H], compress: true });

      for (let i = 0; i < SLIDES.length; i++) {
        const SlideComp = SLIDE_COMPONENTS[SLIDES[i].id];
        wrapper.innerHTML = '';
        const el = document.createElement('div');
        el.style.cssText = `width:${DESIGN_W}px;height:${DESIGN_H}px;`;
        wrapper.appendChild(el);

        const root = createRoot(el);
        root.render(<SlideComp />);
        await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
        await new Promise(r => setTimeout(r, 200));

        const canvas = await html2canvas(el, { width: DESIGN_W, height: DESIGN_H, backgroundColor: BG, scale: 2, useCORS: true, logging: false });
        if (i > 0) pdf.addPage();
        pdf.addImage(canvas.toDataURL('image/jpeg', 0.93), 'JPEG', 0, 0, DESIGN_W, DESIGN_H);
        // Team slide: clickable link over SMU text in Harry's bio
        if (SLIDES[i].id === 'team') {
          pdf.link(482, 116, 396, 50, { url: 'https://www.smu.ca/withoutlimits/basketball-empowerment.html' });
        }
        // CTA slide: clickable link over the scheduling URL text
        if (SLIDES[i].id === 'cta') {
          pdf.link(150, 375, 450, 40, { url: 'https://cal.com/bigger-than-hoop-dreams' });
        }
        root.unmount();
      }

      document.body.removeChild(wrapper);
      pdf.save('bthd-investor-deck.pdf');
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  const goTo = (id: SlideId) => {
    setActiveSlide(id);
    setNavOpen(false);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#040810',
      fontFamily: "'Barlow', 'Barlow Condensed', -apple-system, BlinkMacSystemFont, sans-serif",
      color: '#fff',
    }}>
      {/* ── Fullscreen presenter overlay ─────────────────────────────────── */}
      {fullscreen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: `min(100vw, calc(100vh * ${DESIGN_W / DESIGN_H}))` }}>
            <ScaledSlide><ActiveComponent /></ScaledSlide>
          </div>
          <button
            onClick={() => setFullscreen(false)}
            style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.08)', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: 12, fontWeight: 700, padding: '6px 10px', borderRadius: 5, cursor: 'pointer', letterSpacing: 1 }}
          >ESC</button>
          <div
            onClick={() => setActiveSlide(prev => { const idx = SLIDES.findIndex(s => s.id === prev); return idx > 0 ? SLIDES[idx - 1].id : prev; })}
            style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '35%', cursor: 'w-resize' }}
          />
          <div
            onClick={() => setActiveSlide(prev => { const idx = SLIDES.findIndex(s => s.id === prev); return idx < SLIDES.length - 1 ? SLIDES[idx + 1].id : prev; })}
            style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '35%', cursor: 'e-resize' }}
          />
          <div style={{ position: 'absolute', bottom: 14, left: '50%', transform: 'translateX(-50%)', fontSize: 11, color: 'rgba(255,255,255,0.3)', letterSpacing: 1 }}>
            {currentIndex + 1} / {SLIDES.length}
          </div>
        </div>
      )}
      {/* Toolbar */}
      <div className="deck-toolbar" style={{
        position: 'sticky', top: 0, zIndex: 200,
        background: '#080d16',
        borderBottom: `1px solid ${BORDER}`,
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 16px',
        flexWrap: 'wrap',
      }}>
        {/* Hamburger on mobile */}
        <button
          onClick={() => setNavOpen(o => !o)}
          style={{
            display: 'none',
            padding: '6px 10px', borderRadius: 6, border: `1px solid ${BORDER}`,
            background: 'transparent', color: '#fff', cursor: 'pointer',
            fontSize: 16, fontWeight: 700,
            // shown via media query hack below — using inline style override
          }}
          className="deck-hamburger"
          aria-label="Menu"
        >☰</button>

        <img src="/bthd-email-sig-300x80-transparent.png" alt="BTHD" style={{ height: 22, width: 'auto' }} />
        <div style={{ fontSize: 11, color: DIM, flex: 1, whiteSpace: 'nowrap' }}>
          Pitch Deck · {SLIDES.length} Slides
        </div>

        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          {(['builder', 'present'] as const).map(m => (
            <button
              key={m}
              onClick={() => setMode(m)}
              style={{
                padding: '5px 10px', borderRadius: 6, border: 'none', cursor: 'pointer',
                background: mode === m ? RED : 'rgba(255,255,255,0.06)',
                color: mode === m ? '#fff' : DIM,
                fontSize: 11, fontWeight: 700,
              }}
            >{m === 'builder' ? 'Builder' : 'All'}</button>
          ))}
          <button
            onClick={() => setFullscreen(true)}
            style={{
              padding: '5px 10px', borderRadius: 6, border: 'none', cursor: 'pointer',
              background: 'rgba(255,255,255,0.06)', color: DIM,
              fontSize: 11, fontWeight: 700,
            }}
          >Present</button>
        </div>

        <button
          onClick={handleExportPDF}
          disabled={exporting}
          style={{
            padding: '5px 12px', borderRadius: 6, border: `1px solid ${BORDER}`,
            background: 'transparent', color: exporting ? DIM : '#fff',
            cursor: exporting ? 'wait' : 'pointer',
            fontSize: 11, fontWeight: 700, flexShrink: 0,
          }}
        >{exporting ? 'Exporting…' : 'PDF'}</button>
      </div>

      {/* Mobile nav overlay */}
      {navOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 150,
          background: 'rgba(0,0,0,0.7)',
        }} onClick={() => setNavOpen(false)}>
          <div
            onClick={e => e.stopPropagation()}
            style={{
              position: 'absolute', top: 48, left: 0, bottom: 0,
              width: 220, background: '#080d16',
              borderRight: `1px solid ${BORDER}`,
              overflowY: 'auto', paddingTop: 8,
            }}
          >
            {SLIDES.map(slide => (
              <button
                key={slide.id}
                onClick={() => goTo(slide.id)}
                style={{
                  width: '100%', textAlign: 'left',
                  padding: '11px 20px', border: 'none', cursor: 'pointer',
                  background: activeSlide === slide.id ? `${RED}18` : 'transparent',
                  borderLeft: `3px solid ${activeSlide === slide.id ? RED : 'transparent'}`,
                  color: activeSlide === slide.id ? '#fff' : DIM,
                  fontSize: 13, fontWeight: activeSlide === slide.id ? 700 : 400,
                }}
              >{slide.label}</button>
            ))}
          </div>
        </div>
      )}

      {mode === 'builder' ? (
        <div style={{ display: 'flex', height: 'calc(100vh - 49px)' }}>
          {/* Desktop sidebar nav */}
          <div className="deck-nav" style={{
            width: 180, flexShrink: 0,
            background: '#080d16',
            borderRight: `1px solid ${BORDER}`,
            overflowY: 'auto', padding: '16px 0',
          }}>
            {SLIDES.map(slide => (
              <button
                key={slide.id}
                onClick={() => setActiveSlide(slide.id)}
                style={{
                  width: '100%', textAlign: 'left',
                  padding: '10px 20px', border: 'none', cursor: 'pointer',
                  background: activeSlide === slide.id ? `${RED}18` : 'transparent',
                  borderLeft: `3px solid ${activeSlide === slide.id ? RED : 'transparent'}`,
                  color: activeSlide === slide.id ? '#fff' : DIM,
                  fontSize: 12, fontWeight: activeSlide === slide.id ? 700 : 400,
                  transition: 'all 0.15s',
                }}
              >{slide.label}</button>
            ))}
          </div>

          {/* Slide preview */}
          <div style={{
            flex: 1, overflowY: 'auto',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', padding: '24px 16px', gap: 20,
          }}>
            <div style={{ width: '100%', maxWidth: 960 }}>
              <ScaledSlide>
                <ActiveComponent />
              </ScaledSlide>
            </div>
            {/* Navigation arrows */}
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <button
                onClick={() => currentIndex > 0 && setActiveSlide(SLIDES[currentIndex - 1].id)}
                disabled={currentIndex === 0}
                style={{
                  padding: '8px 20px', borderRadius: 6, border: `1px solid ${BORDER}`,
                  background: 'transparent', color: currentIndex === 0 ? 'rgba(255,255,255,0.2)' : '#fff',
                  cursor: currentIndex === 0 ? 'default' : 'pointer', fontSize: 13, fontWeight: 700,
                }}
              >← Prev</button>
              <span style={{ fontSize: 12, color: DIM }}>
                {currentIndex + 1} / {SLIDES.length}
              </span>
              <button
                onClick={() => currentIndex < SLIDES.length - 1 && setActiveSlide(SLIDES[currentIndex + 1].id)}
                disabled={currentIndex === SLIDES.length - 1}
                style={{
                  padding: '8px 20px', borderRadius: 6, border: `1px solid ${BORDER}`,
                  background: currentIndex < SLIDES.length - 1 ? RED : 'transparent',
                  color: '#fff',
                  cursor: currentIndex === SLIDES.length - 1 ? 'default' : 'pointer', fontSize: 13, fontWeight: 700,
                }}
              >Next →</button>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ padding: '24px 16px', display: 'flex', flexDirection: 'column', gap: 32 }}>
          {SLIDES.map(slide => {
            const SlideComp = SLIDE_COMPONENTS[slide.id];
            return (
              <div key={slide.id} className="deck-slide-wrapper" style={{ maxWidth: 1100, margin: '0 auto', width: '100%' }}>
                <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 3, color: DIM, textTransform: 'uppercase', marginBottom: 8 }}>
                  {slide.label}
                </div>
                <ScaledSlide>
                  <SlideComp />
                </ScaledSlide>
              </div>
            );
          })}
        </div>
      )}

      {/* Responsive styles injected globally */}
      <style>{`
        @media (max-width: 640px) {
          .deck-nav { display: none !important; }
          .deck-hamburger { display: flex !important; }
        }
        @media (min-width: 641px) {
          .deck-hamburger { display: none !important; }
        }
      `}</style>
    </div>
  );
}
